import type { Hub } from "@portal/api/types";
import { selectDefaultHub } from "./utils";

type Scope = "all" | "single";
interface IActiveHub {
  scope: Scope;
  id: string[];
  name: string;
  longitude: number;
  latitude: number;
  externalId: string;
  xActiveHub: string;
}

export type ActiveHubOptions = {
  scope: Scope;
  defaultHub?: Hub;
};

const DEFAULT_OPTIONS = {
  scope: "single",
} as const satisfies Pick<ActiveHubOptions, "scope">;
export class ActiveHub implements IActiveHub {
  scope: Scope;
  /**
   * `id` consumed across the app, and used as query keys and request parameters
   * so this must be unique
   */
  id: string[];
  /**
   * `name` is displayed in the UI and used as the label for the active hub, so it must be
   * human readable and unique
   */
  name: string;
  longitude: number;
  latitude: number;
  externalId: string;
  xActiveHub: string;
  defaultHub: Hub;
  private hubs: Hub[];

  constructor(hubs: Hub[], opts: Partial<ActiveHubOptions> = {}) {
    const {
      scope = DEFAULT_OPTIONS.scope,
      defaultHub = selectDefaultHub(hubs),
    } = opts;
    this.hubs = scope === "all" ? hubs : [defaultHub];
    this.scope = scope;
    this.id = this.generateIdFromHubs();
    this.name = this.generateNameFromHubs();
    this.longitude = this.generateLongitudeFromHubs();
    this.latitude = this.generateLatitudeFromHubs();
    this.externalId = this.generateExternalIdFromHubs();
    this.xActiveHub = this.hubs[0].id;
    this.defaultHub = defaultHub;
  }

  private generateNameFromHubs() {
    if (this.scope === "all") {
      return "All Hubs";
    }
    return this.hubs[0].name;
  }

  private generateLongitudeFromHubs() {
    /**
     * @todo: Generate center from multiple hub selection
     */
    return this.hubs[0].longitude;
  }

  private generateLatitudeFromHubs() {
    /**
     * @todo: Generate center from multiple hub selection
     */
    return this.hubs[0].latitude;
  }

  private generateExternalIdFromHubs() {
    /**
     * @todo: Generate external id from multiple hub selection
     */
    return this.hubs[0].externalId;
  }

  private generateIdFromHubs() {
    return this.hubs.map((hub) => hub.id);
  }
}
